/**
 * Subscription entity class
 */

import { ascSortOrder } from 'app/helpers/entity'
import { clone } from '../../../app/helpers/object'
import { PLATFORM_TYPES, SUBSCRIPTION_PRODUCT_TYPES_FOR_DISCOUNT } from '../../../app/core/constants'
import { dateAsUTC } from '../../../app/helpers/date'

/**
 * SubscriptionFactory
 * returns Subscription entity class
 *
 * @class SubscriptionFactory
 * @memberof mwApp
 * @see mwApp
 * @mixes models.FileUploadMixin
 * @return {Function} {@link models.Subscription} class
 *
 * @ngdoc factory
 * @name mwApp.factory:SubscriptionFactory
 */
/* @ngInject */
export default function SubscriptionFactory ($http, $injector, SubscriptionAccessory, Entity, Type, Amount, Currency, VODSet, Network, ChannelSet, ShowSet, LiveEventSet, FileUploadMixin, onOffMixin, alertLiteSvc, authSvc) {
  /* === Subscription class === */

  /**
   * Subscription model
   *
   * @class Subscription
   * @classdesc Subscription model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @return {models.Subscription} Subscription entity
   */
  function Subscription () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }

  Subscription.prototype = Object.create(Entity.prototype)
  Subscription.prototype.constructor = Subscription

  /* === Subscription instance methods & fields === */

  Object.assign(Subscription.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Subscription#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      amounts: ['Amount'],
      avod: Boolean,

      // ↓ auxiliary fields, there are no such fields in real model on the server
      baseAmount: 'Amount', // PRICE
      basePrice: 0, // PRICE
      deviceAmounts: ['Amount'], // EXTRA_DEVICE
      extraAmounts: ['Amount'], // EXTRA_CHARGE
      taxAmounts: ['Amount'], // TAX_VAT
      maxDevices: 0,
      // ↑

      assignedAccessories: ['SubscriptionAccessory'],
      autoPay: true,
      autopayRetryDaysLimit: Number,
      currency: 'Currency',
      custom: false,
      coverImageUrl: String,
      description: String,
      allowDolbyAtmos: true,
      dueDate: String,
      enabled: true,
      free: false,
      hasCarousels: Boolean,
      length: 1,
      lengthType: Type(String('Days')),
      network: 'Network',
      networkId: Number,
      subscriptionType: Type(String('Subscription')),
      user: 'User',
      identifier: String,
      ecommerceEnabled: Boolean,
      usedBy: [String],
      freeContentSets: {
        vodSets: ['VODSet'],
        channelSets: ['ChannelSet'],
        tvShowSets: ['ShowSet'],
        liveEventSets: ['LiveEventSet'],
        multiSets: ['MultiSet']
      },
      paidContentSets: {
        vodSets: ['VODSet'],
        channelSets: ['ChannelSet'],
        tvShowSets: ['ShowSet'],
        liveEventSets: ['LiveEventSet'],
        multiSets: ['MultiSet']
      },
      addFreeContentSets: {
        vodSets: ['VODSet'],
        channelSets: ['ChannelSet'],
        tvShowSets: ['ShowSet'],
        liveEventSets: ['LiveEventSet'],
        multiSets: ['MultiSet']
      },
      addPaidContentSets: {
        vodSets: ['VODSet'],
        channelSets: ['ChannelSet'],
        tvShowSets: ['ShowSet'],
        liveEventSets: ['LiveEventSet'],
        multiSets: ['MultiSet']
      },
      removeFreeContentSets: {
        vodSets: ['VODSet'],
        channelSets: ['ChannelSet'],
        tvShowSets: ['ShowSet'],
        liveEventSets: ['LiveEventSet'],
        multiSets: ['MultiSet']
      },
      removePaidContentSets: {
        vodSets: ['VODSet'],
        channelSets: ['ChannelSet'],
        tvShowSets: ['ShowSet'],
        liveEventSets: ['LiveEventSet'],
        multiSets: ['MultiSet']
      },
      freePartnerProducts: ['PartnerProduct'],
      paidPartnerProducts: ['PartnerProduct'],
      addFreePartnerProducts: ['PartnerProduct'],
      addPaidPartnerProducts: ['PartnerProduct'],
      removeFreePartnerProducts: ['PartnerProduct'],
      removePaidPartnerProducts: ['PartnerProduct'],
      perItemBuyPriceRequired: Boolean,
      perItemRentPriceRequired: Boolean,
      rent: {
        amount: Number
      },
      buy: {
        amount: Number
      },
      trial: false,
      trialLength: 1,
      trialLengthType: Type(String('Days')),
      requirePaymentData: Boolean,
      universalPlusAccessEnabled: false,
      useNetworkPlatforms: true,
      platforms: Type.CSV(';'),
      heading: String,
      promoLabel: String,
      periodUnlimited: false,
      pricingRules: [{
        id: Number,
        enabled: Boolean,
        sortOrder: Number,
        value: Number,
        productType: String,
        accessory: { id: Number, name: String, amount: Number },
        contentAddon: 'ChannelSet',
        conditionProductType: String,
        conditionAccessories: [{ id: Number, name: String }],
        conditionContentAddons: ['ChannelSet']
      }],
      concurrentStreamsLimit: 1,
      // subscription limitations
      allowCastOption: true,
      allowCellularOnly: false,
      allowOfflinePlayback: false,
      maxQuality: '_4K',
      exclusivePaymentSystemSetting: {
        id: Number,
        assignedPaymentSystems: [{ paymentSystemId: Number, paymentSystemType: String }]
      },
      $exclusivePaymentSystemSettingCached: {
        id: Number,
        assignedPaymentSystems: [{ paymentSystemId: Number, paymentSystemType: String }]
      },

      // temp fields
      $promoLabelEnabled: false,
      $periodType: String,
      $dueDate: Date,
      $cachedAutoPay: Boolean,
      $cachedFree: Boolean,
      $cachedPeriodType: String,
      $checkedDC: false,
      $currentEnabled: Boolean,
      $discountCampaigns: [],
      $ecwidSettings: 'Ecwid',
      $extraDeviceWithBaseAmounts: [],
      $subscriptionBasePrice: 0,
      $trialLabel: '',
      $amountsCount: 1,
      $comingChannelSetIds: [Number],
      $onRemoveMessages: {},
      $isInappSubscription: Boolean,
      $isGuestSubscription: Boolean,
      $cashedCurrencyId: String,
      $paidContentSetsArr: ['ChannelSet'],
      $paidPartnerProducts: ['PartnerProduct'],
      $freePartnerProducts: ['PartnerProduct']
    },

    /**
     * url template
     *
     * @memberof models.Subscription#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Subscription#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/monetization-plans/subscriptions/{{::id}}',

    /**
     *
     *
     * @returns {*}
     * @private
     */
    _getNetworkAccessoriesByCurrencyId: function (currencyId) {
      return $http.get(this.network._url + '/accessories', { params: { currencyId } })
        .data()
        .then((resp) => resp.payload.map(item => new SubscriptionAccessory(item)))
        .catch(alertLiteSvc.alertError)
    },

    /**
     * Split amounts by types to auxiliary fields and sort them
     *
     * @memberof models.Subscription#
     * @override
     */
    _prepare: function () {
      if (!authSvc.hasEnabledFlag('FEATURE_RADIO_CHANNELS_ENABLED') && this.paidContentSets) {
        this.paidContentSets.channelSets = this.paidContentSets.channelSets.filter(set => set?.type !== 'RADIO_CHANNEL_SET') || []
      }
      if (authSvc.hasEnabledFlag('FEATURE_CONCURRENT_STREAMS_LIMITATION_ENABLED') && !this.concurrentStreamsLimit) {
        this.concurrentStreamsLimit = 1
      }
      this.$cashedCurrencyId = this.currency?.id
      this.$isGuestSubscription = this.usedBy.includes('GUEST_MODE')
      this.$isInappSubscription = this.usedBy.includes('IN_APP_REGISTRATION')
      if (!this.network) {
        this.network = new Network({ id: this.networkId })
      }
      if (this.periodUnlimited) {
        this.$periodType = 'UNLIMITED'
      } else if (this.dueDate) {
        this.$periodType = 'DUE_DATE'
        this.$dueDate = new Date(this.dueDate)
      } else {
        this.$periodType = 'DEFAULT'
      }

      if (!this.exclusivePaymentSystemSetting) {
        this.exclusivePaymentSystemSetting = {
          assignedPaymentSystems: []
        }
      }

      this.$exclusivePaymentSystemSettingCached = angular.copy(this.exclusivePaymentSystemSetting)

      this._setOnRemoveMessage()
      if (this.amounts && this.amounts.length > 0) {
        for (var i = this.amounts.length; i--;) {
          var amount = this.amounts[i]
          var type = amount.amountType

          if (type === 'PRICE') {
            this.baseAmount = amount
          } else if (type === 'EXTRA_DEVICE') {
            this.deviceAmounts.push(amount)
          } else if (type === 'EXTRA_CHARGE') {
            this.extraAmounts.push(amount)
          } else if (type === 'TAX_VAT') {
            this.taxAmounts.push(amount)
          }
        }
        if (this.deviceAmounts.find(a => a.amount > 0)) {
          this.$amountsCount = 0
        } else {
          this.$amountsCount = this.deviceAmounts.length + 1
        }
        this.amounts.length = 0

        if (!this.freeContentSets) {
          this.freeContentSets = {
            vodSets: [],
            channelSets: [],
            tvShowSets: [],
            liveEventSets: [],
            multiSets: []
          }
        }

        this.$paidPartnerProducts = angular.copy(this.paidPartnerProducts)
        this.$freePartnerProducts = angular.copy(this.freePartnerProducts)

        if (!this.paidContentSets) {
          this.paidContentSets = {
            vodSets: [],
            channelSets: [],
            tvShowSets: [],
            liveEventSets: [],
            multiSets: []
          }
        } else {
          this.$comingChannelSetIds = this.paidContentSets.channelSets.map(cs => cs.id)
        }

        this.$promoLabelEnabled = !!this.promoLabel

        this.deviceAmounts.sort(ascSortOrder)
        this.extraAmounts.sort(ascSortOrder)
        this.taxAmounts.sort(ascSortOrder)
      }

      // prepare for discounts

      for (const productType in SUBSCRIPTION_PRODUCT_TYPES_FOR_DISCOUNT) {
        this['$pricingRules' + productType] = this.pricingRules.filter(rule => rule.productType === productType)
      }

      if (this.baseAmount?.amount === 0) {
        this.free = true
      }
      this.$currentEnabled = this.enabled

      this.$cachedPeriodType = this.$periodType
      this.$cachedAutoPay = this.autoPay
      this.$cachedFree = this.free

      // end prepare for discounts
    },

    /**
     * Get underlying object
     *
     * @memberof models.Subscription#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      this._setSubscriptionPeriodType()
      if (this.$amountsCount) {
        this.deviceAmounts = []
        for (let i = 0; i < this.$amountsCount; i++) {
          this.deviceAmounts.push(new Amount({
            amountType: 'EXTRA_DEVICE',
            label: i + 1,
            name: i + 1,
            amount: 0
          }))
        }
      }
      this.deviceAmounts = this.deviceAmounts.sort((a, b) => {
        if (a.amount < b.amount) {
          return -1
        }
        if (a.amount < b.amount) {
          return 1
        }

        return 0
      })

      this.addPaidPartnerProducts = this.$paidPartnerProducts.filter(product => !this.paidPartnerProducts.find(p => p.id === product.id))
      this.removePaidPartnerProducts = this.paidPartnerProducts.filter(product => !this.$paidPartnerProducts.find(p => p.id === product.id))
      this.paidPartnerProducts = this.paidPartnerProducts.filter(product => this.$paidPartnerProducts.find(p => p.id === product.id))
      this.paidPartnerProducts.forEach((product) => {
        const currentProduct = this.$paidPartnerProducts.find(p => p.id === product.id)
        product.monetization.amount = currentProduct.monetization.amount
      })

      this.addFreePartnerProducts = this.$freePartnerProducts.filter(product => !this.freePartnerProducts.find(p => p.id === product.id))
      this.removeFreePartnerProducts = this.freePartnerProducts.filter(product => !this.$freePartnerProducts.find(p => p.id === product.id))
      this.freePartnerProducts = this.freePartnerProducts.filter(product => this.$freePartnerProducts.find(p => p.id === product.id))

      this.deviceAmounts.forEach((d, i) => {
        d.sortOrder = i
      })
      var ret = Entity.prototype._object.apply(this, arguments)
      ret.amounts = ret.baseAmount ? [{ ...ret.baseAmount, amount: ret.free ? 0 : ret.baseAmount.amount }] : []
      Array.prototype.push.apply(ret.amounts, ret.deviceAmounts)
      Array.prototype.push.apply(ret.amounts, ret.extraAmounts)
      Array.prototype.push.apply(ret.amounts, ret.taxAmounts)

      delete ret.baseAmount // remove auxiliary field
      delete ret.basePrice // remove auxiliary field
      delete ret.deviceAmounts // remove auxiliary field
      delete ret.extraAmounts // remove auxiliary field
      delete ret.taxAmounts // remove auxiliary field
      delete ret.maxDevices

      delete ret.network // remove network from result object (fix for MW-1394)

      // set 'autoPay' and 'length' to false|0 for each type, except 'Subscription'
      if (ret.subscriptionType !== 'Subscription') {
        ret.autoPay = false
        ret.length = 0
        ret.lengthType = null
      } else if (ret.periodUnlimited) {
        ret.autoPay = false
      }

      // set subscription ID to -1 for new subscription, in order to make link between subscription and amounts inside
      if (!ret.id) {
        ret.id = -1
      }

      // set sortOrder for each amount, and create backlink for subscription itself
      ret.amounts = ret.amounts.filter((a, i) => i !== 1)
      ret.amounts.forEach(function (amount, i) {
        amount.id = amount.id || -i - 1
        amount.sortOrder = i
        amount.subscription = ret.id
        if (ret.subscriptionType !== 'Subscription') {
          amount.excludeTax = false
        }
      })
      Object.assign(ret, { assignedAccessories: this.assignedAccessories })
      this._objectForSubscriptionDiscountsSettings(ret)

      return ret
    },

    _setSubscriptionPeriodType: function () {
      switch (this.$periodType) {
        case 'DUE_DATE':
          this.dueDate = dateAsUTC(this.$dueDate, 'YYYY-MM-DD')
          this.periodUnlimited = false
          this.length = 0
          this.lengthType = null
          this.autoPay = false
          break
        case 'DEFAULT':
          this.periodUnlimited = false
          this.dueDate = null
          break
        default:
          this.periodUnlimited = true
          this.length = 0
          this.lengthType = null
          this.dueDate = null
          this.autoPay = false
      }
    },

    _objectForSubscriptionDiscountsSettings (ret) {
      const discountsProductTypes = SUBSCRIPTION_PRODUCT_TYPES_FOR_DISCOUNT

      ret.pricingRules = []
      for (const productType in discountsProductTypes) {
        ret.pricingRules.push(...this['$pricingRules' + productType])
      }
    },

    _setOnRemoveMessage: function () {
      const message = 'Are you sure you want to delete Subscription permanently?'
      const messageCustomization = 'Subscription has a banner/featured carousel. If it is disabled, banners and featured items according to this subscription will be removed from the carousels.<br><br>'
      const message11 = 'Do You want to change Guest mode and In-App Registration setting{isMultiorg}?'
      const message10 = 'Do You want to change In-app registrations setting{isMultiorg}?'
      const message01 = 'Do You want to change Guest mode setting{isMultiorg}?'

      const usedBy = ['GUEST_MODE', 'IN_APP_REGISTRATION', 'VOUCHER_BATCH', 'ACTIVE_SUBSCRIBER']
      const messages = {
        1100: `Currently Subscription has active Subscribers and selected for Voucher Batch(es). If deleted, access to Client App will be disabled for Subscribers activated with Subscription.<br><br>${message}`,
        1000: `Currently Subscription has active Subscribers. If deleted, access to Client App will be disabled for Subscribers activated with this Subscription.<br><br>${message}`,
        100: `Currently Subscription is selected for Voucher Batch(es). If deleted, access to Client App will be disabled for Subscribers activated with Subscription.<br><br>${message}`,
        0: message
      }
      messages[1111] = messages[1011] = messages[111] = messages[11] = `This Subscription is a default Subscription for Guest Mode and for In-App Registration. To remove or disable it You should change Guest Mode and In-App Registration settings{isMultiorg}.<br><br>${message11}`
      messages[1110] = messages[1010] = messages[110] = messages[10] = `This Subscription is a default Subscription for In-app registration. To remove or disable it You should change In-app registrations settings{isMultiorg}.<br><br>${message10}`
      messages[1101] = messages[1001] = messages[101] = messages[1] = `This Subscription is a default Subscription for Guest Mode. To remove or disable it You should change Guest Mode settings{isMultiorg}.<br><br>${message01}`

      const messageNumber = usedBy.reduce((acc, el, index) => acc + (this.usedBy.some(i => i === el) && Math.pow(10, index)), 0)
      this.$onRemoveMessages = {}
      this.$onRemoveMessages.general = (this.hasCarousels ? messageCustomization : '') + (messages[messageNumber] || message)
      this.$onRemoveMessages.withoutEditGeneralPermissions = 'This Subscription is a default Subscription for In-app registration/Guest mode. To remove or disable it contact your administrator.'
    },

    /**
     * @param {string} productType
     * @param {*} pricingRule
     * @param parameter
     */
    _getDiscountProductProperty: function (productType, pricingRule, parameter = 'id') {
      return (productType === 'ACCESSORY' && pricingRule.accessory && pricingRule.accessory[parameter]) ||
        (productType === 'CONTENT_ADDON' && pricingRule.contentAddon && pricingRule.contentAddon[parameter])
    },

    /**
     * @param {string} type
     * @param item
     */
    _countDiscountRulesForSubscriptionProduct: function (type, item) {
      const discountsProductTypes = SUBSCRIPTION_PRODUCT_TYPES_FOR_DISCOUNT
      const assignedInRules = []

      for (const productType in discountsProductTypes) {
        const pricingRules = this['$pricingRules' + productType]
        for (let ruleIndex = 0; ruleIndex < pricingRules?.length; ruleIndex++) {
          const pricingRule = pricingRules[ruleIndex]
          const discountedProductId = this._getDiscountProductProperty(productType, pricingRule)
          if (productType === type) {
            if (discountedProductId === item.id) {
              assignedInRules.push({ ...pricingRule, ruleIndex })
              continue
            }
          }
          if (pricingRule.conditionProductType === type) {
            const arr = pricingRule.$conditionItems?.filter(({ id }) => id === item.id)
            if (arr?.length) { assignedInRules.push({ ...pricingRule, ruleIndex }) }
          }
        }
      }

      return assignedInRules
    },

    /**
     * Get amount of supported devices
     *
     * @memberof models.Subscription#
     * @return {Number}
     */
    _deviceSupport: function () {
      return (this.deviceAmounts ? this.deviceAmounts.length + 1 : 0)
    },

    /**
     * Get platforms array with checked flags, depending on network platforms array
     *
     * @memberof models.Subscription#
     * @return {Object[]}
     */
    _enhancePlatforms: function () {
      const ret = clone(PLATFORM_TYPES.filter((p) => {
        return !(!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED') && p.name === 'WEB')
      }))
      if (this._isNew()) {
        for (let i = ret.length; i--;) {
          ret[i].checked = false
        }
      } else if (this.platforms && this.platforms.length) {
        for (let i = ret.length; i--;) {
          ret[i].checked = this.platforms.indexOf(ret[i].name) !== -1
        }
      }
      return ret
    },

    /**
     * Return array with subscription prices
     *
     * @memberof models.Subscription#
     * @return {Object[]}
     */
    __pricesArray: [],
    _pricesArray: function () {
      // add base total price
      var ret = []

      ret.push({
        name: 'Total',
        amount: this.$subscriptionBasePrice || 0
      })
      // add prices for additional devices
      for (var i = 0; i < this.$extraDeviceWithBaseAmounts.length; i++) {
        ret.push({
          name: 'Total + Device ' + (i + 1),
          amount: this.$extraDeviceWithBaseAmounts[i]
        })
      }

      function same (a, b) {
        if (a.length !== b.length) {
          return false
        }

        for (var i = a.length; i--;) {
          if (a[i].amount !== b[i].amount) {
            return false
          }
        }

        return true
      }

      // if array has changed - replace it
      // we need this trick in order to use _pricedArray() function inside of ng-repeat
      if (!same(this.__pricesArray, ret)) {
        this.__pricesArray = ret
      }

      return this.__pricesArray
    },
    /**
     * loading channels for subscription, which can be a start channel
     * @returns {[Channel]}
     * @private
     */
    _loadChannels: function () {
      return $http.get(this._url + '/channels/tv/extra/enabled')
        .data()
        .then((resp) => resp.payload)
        .catch(alertLiteSvc.alertError)
    }
  }, onOffMixin, FileUploadMixin)

  /* === Subscription static methods & fields === */

  /**
   * Generate url
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {String} [url]
   * @return {String}
   */
  Subscription.generateUrl = function (url = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    return `${cast.prototype.$_urlTemplate(o)}` + url
  }

  /**
   * Load VOD-sets
   *
   * @param {Object} [o] - object over which Url will be interpolated
   * @param {Object} [state]
   * @return {String}
   */
  Subscription.loadVODSets = function (o, state) {
    const params = {
      count: state.count,
      page: state.page
    }

    if (state?.name) {
      params.name = state.name
    }

    return $http.get(Subscription.generateUrl('\\vodsets?available', o) + '', { params: Object.assign(params) })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Load VOD-sets
   *
   * @return {String}
   * @param subscription
   * @param onlyRecurring
   */
  Subscription.loadDiscountCampaigns = function (subscription, onlyRecurring = false) {
    return $http.get(Subscription.generateUrl('\\discount-campaigns', subscription) + '', { params: { onlyRecurring } })
      .data()
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * @param {string|number} type
   * @param {{networkId, id: number}} o
   * @param {*} basicSets
   * @param {*} addonSets
   */
  Subscription.checkIntersection = function (type, o, basicSets, addonSets) {
    if (basicSets.length + addonSets.length < 1) { return Promise.resolve() }

    const types = {
      vodSets: { url: 'vodsets' },
      tvShowSets: { url: 'tv-show-sets' },
      channelSets: { url: 'channelsets' },
      liveEventSets: { url: 'live-event-sets' }
    }

    const url = `/${types[type].url}/intersection`

    const allSets = {};
    [...basicSets, ...addonSets].forEach(set => {
      set.conflicts = []
      allSets[set.id] = set
    })

    return $http.post(Subscription.generateUrl(url, o), { basicSets, addonSets })
      .data()
      .then((response) => {
        const conflicts = response?.payload

        if (conflicts?.length) {
          for (const conflict of conflicts) {
            if (type === 'tvShowSets' && conflict.content[0].parent) {
              const newConflictContent = {}
              for (const conflictItem of conflict.content) {
                if (!newConflictContent[conflictItem.parent.id]) {
                  newConflictContent[conflictItem.parent.id] = { name: conflictItem.parent.name, subItems: [] }
                }
                newConflictContent[conflictItem.parent.id].subItems.push(conflictItem)
              }
              conflict.content = Object.keys(newConflictContent).map(key => (
                { id: key, name: newConflictContent[key].name, subItems: newConflictContent[key].subItems }))
            }
            for (const n of [0, 1]) {
              const setId = conflict[`id${1 + n}`]
              const conflictSetID = conflict[`id${2 - n}`]
              const conflictSet = allSets[conflictSetID]
              const conflictToSet = { ...conflict, conflictSet }
              allSets[setId].conflicts.push(conflictToSet)
            }
          }
        }
        return !!conflicts?.length
      })
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * @param {*&{baseAmount: (*&{amount: number})}} subscription
   */
  Subscription.calculate = function (subscription) {
    let amounts = []
    amounts.push(subscription.baseAmount)
    if (subscription.subscriptionType === 'Subscription') {
      amounts = amounts.concat(subscription.deviceAmounts)
    }
    amounts.forEach((a, i) => {
      a.sortOrder = i
    })
    return $http.post(Subscription.generateUrl('/calculate', {
      ...subscription,
      id: subscription.id || 0
    }), { currency: subscription.currency, subscriptionType: subscription.subscriptionType, amounts })
      .data()
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  // copy all static methods from Entity class
  Object.assign(Subscription, Entity)
  Subscription._name = 'Subscription'
  Subscription.init()

  // permissions
  Subscription.access = {
    view: 'viewSubscription createSubscriptionPayment |',
    create: 'createSubscription',
    edit: 'editSubscription',
    delete: 'deleteSubscription'
  }

  // extra
  Subscription.extra = {
    currenciesAllowedForNetwork: ['Currency'],
    availableAccessories: ['SubscriptionAccessory'],
    platforms: String
  }

  return Subscription
}
